/**
 * LA 2028 Venue Template JavaScript
 * Handles map initialization, product grouping, and filtering
 */

(function() {
  'use strict';

  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    if (!window.LA2028VenueData) {
      console.error('LA2028VenueData not found');
      return;
    }

    loadVenueData();
  }

  /**
   * Load venue data and initialize all components
   */
  function loadVenueData() {
    const venueDataUrl = getAssetUrl('la2028-venues-complete.json');

    fetch(venueDataUrl)
      .then(response => response.json())
      .then(data => {
        const venues = data.venues;
        const zones = data.zones;

        // Find the current venue
        const venue = venues.find(v => v.handle === window.LA2028VenueData.venueHandle);
        if (!venue) {
          console.error('Venue not found');
          return;
        }

        const zoneInfo = zones[venue.zone] || {
          color: '#6366f1',
          emoji: '🏟️',
          description: ''
        };

        // Update page with venue data
        updateVenueInfo(venue, zoneInfo);

        // Set zone color CSS variables
        setZoneColors(zoneInfo.color);

        // Find nearby venues
        const nearbyVenues = findNearbyVenues(venues, venue, 10);

        // Update sidebar
        updateSidebar(venue, zoneInfo, nearbyVenues);

        // Initialize product grouping and filtering
        initializeProducts(venue);

        // Listen for map ready event
        const mapElement = document.getElementById('venue-map');
        if (mapElement) {
          mapElement.addEventListener('mapReady', function(event) {
            console.log('Map initialized with', event.detail.nearbyVenues.length, 'nearby venues');
          });
        }
      })
      .catch(error => {
        console.error('Error loading venue data:', error);
      });
  }

  /**
   * Update venue information throughout the page
   */
  function updateVenueInfo(venue, zoneInfo) {
    // Hero section
    document.getElementById('zone-emoji').textContent = zoneInfo.emoji;
    document.getElementById('zone-name').textContent = venue.zone;
    document.getElementById('venue-capacity').textContent = venue.capacity + ' capacity';
    document.getElementById('venue-city').textContent = venue.city;

    const sportsCount = venue.sports.length;
    document.getElementById('sports-count').textContent = sportsCount + ' ' + (sportsCount === 1 ? 'sport' : 'sports');

    // Breadcrumb
    const breadcrumbZone = document.getElementById('breadcrumb-zone');
    if (breadcrumbZone) {
      breadcrumbZone.innerHTML = `<a href="/collections/la-2028-${handleize(venue.zone)}">${venue.zone}</a>`;
    }

    // Zone link section
    document.getElementById('zone-name-cta').textContent = venue.zone;
    document.getElementById('zone-emoji-cta').textContent = zoneInfo.emoji;
    document.getElementById('zone-name-link').textContent = venue.zone;
    document.getElementById('zone-collection-link').href = `/collections/la-2028-${handleize(venue.zone)}`;

    // SEO content
    document.getElementById('seo-capacity').textContent = venue.capacity;
    document.getElementById('seo-city').textContent = venue.city;

    const metroText = venue.metroLines && venue.metroLines.length > 0
      ? venue.metroLines.join(', ')
      : 'public transportation';
    document.getElementById('seo-metro').textContent = metroText;

    const sportsText = venue.sports.length > 1
      ? venue.sports.slice(0, -1).join(', ') + ' and ' + venue.sports[venue.sports.length - 1]
      : venue.sports[0];
    document.getElementById('seo-sports').textContent = sportsText;
  }

  /**
   * Set CSS custom properties for zone colors
   */
  function setZoneColors(color) {
    const root = document.documentElement;
    root.style.setProperty('--zone-color', color);

    // Generate darker shade for gradients
    const darkColor = adjustColorBrightness(color, -20);
    root.style.setProperty('--zone-color-dark', darkColor);
  }

  /**
   * Update sidebar with venue details
   */
  function updateSidebar(venue, zoneInfo, nearbyVenues) {
    // Venue info
    document.getElementById('sidebar-capacity').textContent = venue.capacity;
    document.getElementById('sidebar-address').textContent = venue.address;
    document.getElementById('sidebar-zone').innerHTML = `${zoneInfo.emoji} ${venue.zone}`;

    // Sports hosted
    const sportsContainer = document.getElementById('sidebar-sports');
    sportsContainer.innerHTML = venue.sports.map(sport => {
      const sportHandle = handleize(sport);
      return `<a href="/collections/la-2028-${sportHandle}" class="sport-tag">${sport}</a>`;
    }).join('');

    // Metro lines
    const metroContainer = document.getElementById('sidebar-metro');
    const metroTransport = document.getElementById('metro-lines-transport');

    const metroHTML = venue.metroLines && venue.metroLines.length > 0
      ? venue.metroLines.map(line => {
          const lineColor = getMetroLineColor(line);
          return `
            <div class="metro-line" style="background-color: ${lineColor}20; border-left: 4px solid ${lineColor};">
              🚇 ${line}
            </div>
          `;
        }).join('')
      : '<p style="color: #64748b; font-size: 0.9rem;">Shuttle service available</p>';

    metroContainer.innerHTML = metroHTML;
    if (metroTransport) {
      metroTransport.innerHTML = metroHTML;
    }

    // Nearby venues
    const nearbyContainer = document.getElementById('nearby-venues-list');
    if (nearbyVenues.length > 0) {
      nearbyContainer.innerHTML = nearbyVenues.slice(0, 4).map(v => {
        const distance = calculateDistance(
          venue.coords[0], venue.coords[1],
          v.venue.coords[0], v.venue.coords[1]
        );
        return `
          <a href="/collections/la-2028-venue-${v.venue.handle}" class="nearby-venue-card">
            <div class="nearby-venue-name">${v.venue.name}</div>
            <div class="nearby-venue-distance">📏 ${distance.toFixed(1)} km away</div>
          </a>
        `;
      }).join('');
    } else {
      nearbyContainer.innerHTML = '<p style="color: #64748b; font-size: 0.9rem;">No nearby venues</p>';
    }
  }

  /**
   * Initialize product grouping and filtering
   */
  function initializeProducts(venue) {
    const productsContainer = document.getElementById('initial-products');
    if (!productsContainer) return;

    const productCards = Array.from(productsContainer.querySelectorAll('.product-card'));
    if (productCards.length === 0) return;

    // Collect unique sports, dates, and session types
    const sports = new Set();
    const dates = new Set();
    const sessionTypes = new Set();

    productCards.forEach(card => {
      const sport = card.dataset.sport || 'General';
      const date = card.dataset.date || '';
      const session = card.dataset.session || 'preliminary';

      sports.add(sport);
      if (date) dates.add(date);
      sessionTypes.add(session);
    });

    // Determine grouping strategy
    const isSingleSport = sports.size === 1;
    const isMultiSport = sports.size > 1;

    // Create filters
    createFilters(sports, dates, sessionTypes, isSingleSport);

    // Group products
    if (isSingleSport) {
      // Single sport: group by date, then session type
      groupProductsByDate(productCards, Array.from(dates).sort());
    } else {
      // Multi sport: group by sport, then date
      groupProductsBySport(productCards, Array.from(sports).sort());
    }
  }

  /**
   * Create filter buttons
   */
  function createFilters(sports, dates, sessionTypes, isSingleSport) {
    const filtersContainer = document.getElementById('filters-container');
    if (!filtersContainer) return;

    let filtersHTML = '';

    // Sport filter (always show if multiple sports)
    if (sports.size > 1) {
      filtersHTML += `
        <div class="filter-group">
          <span class="filter-label">Sport:</span>
          <button class="filter-btn active" data-filter-type="sport" data-filter-value="all">
            All Sports
          </button>
          ${Array.from(sports).sort().map(sport =>
            `<button class="filter-btn" data-filter-type="sport" data-filter-value="${sport}">${sport}</button>`
          ).join('')}
        </div>
      `;
    }

    // Session type filter
    if (sessionTypes.size > 1) {
      filtersHTML += `
        <div class="filter-group">
          <span class="filter-label">Session:</span>
          <button class="filter-btn active" data-filter-type="session" data-filter-value="all">
            All Sessions
          </button>
          ${Array.from(sessionTypes).sort().map(session =>
            `<button class="filter-btn" data-filter-type="session" data-filter-value="${session}">
              ${capitalizeFirst(session)}
            </button>`
          ).join('')}
        </div>
      `;
    }

    filtersContainer.innerHTML = filtersHTML;

    // Add filter event listeners
    const filterButtons = filtersContainer.querySelectorAll('.filter-btn');
    filterButtons.forEach(btn => {
      btn.addEventListener('click', function() {
        handleFilterClick(this, isSingleSport);
      });
    });
  }

  /**
   * Handle filter button click
   */
  function handleFilterClick(button, isSingleSport) {
    const filterType = button.dataset.filterType;
    const filterValue = button.dataset.filterValue;

    // Update active state
    const siblings = button.parentElement.querySelectorAll('.filter-btn');
    siblings.forEach(btn => btn.classList.remove('active'));
    button.classList.add('active');

    // Filter products
    const productCards = document.querySelectorAll('.product-card');

    productCards.forEach(card => {
      let show = true;

      // Check all active filters
      const activeFilters = document.querySelectorAll('.filter-btn.active');
      activeFilters.forEach(filter => {
        const type = filter.dataset.filterType;
        const value = filter.dataset.filterValue;

        if (value === 'all') return;

        if (type === 'sport' && card.dataset.sport !== value) {
          show = false;
        }
        if (type === 'session' && card.dataset.session !== value) {
          show = false;
        }
      });

      card.style.display = show ? '' : 'none';
    });

    // Update group visibility and counts
    updateGroupVisibility();
  }

  /**
   * Group products by sport
   */
  function groupProductsBySport(productCards, sports) {
    const container = document.getElementById('products-container');
    container.innerHTML = '';

    sports.forEach(sport => {
      const sportCards = productCards.filter(card => card.dataset.sport === sport);
      if (sportCards.length === 0) return;

      const groupDiv = document.createElement('div');
      groupDiv.className = 'product-group';
      groupDiv.dataset.groupType = 'sport';
      groupDiv.dataset.groupValue = sport;

      const groupHeader = `
        <div class="group-header">
          <h3>🏅 ${sport}</h3>
          <span class="group-count">${sportCards.length}</span>
        </div>
      `;

      const gridDiv = document.createElement('div');
      gridDiv.className = 'product-grid';
      sportCards.forEach(card => gridDiv.appendChild(card));

      groupDiv.innerHTML = groupHeader;
      groupDiv.appendChild(gridDiv);
      container.appendChild(groupDiv);
    });
  }

  /**
   * Group products by date
   */
  function groupProductsByDate(productCards, dates) {
    const container = document.getElementById('products-container');
    container.innerHTML = '';

    if (dates.length === 0) {
      // No dates, show all in one group
      const groupDiv = document.createElement('div');
      groupDiv.className = 'product-group';

      const groupHeader = `
        <div class="group-header">
          <h3>📅 All Events</h3>
          <span class="group-count">${productCards.length}</span>
        </div>
      `;

      const gridDiv = document.createElement('div');
      gridDiv.className = 'product-grid';
      productCards.forEach(card => gridDiv.appendChild(card));

      groupDiv.innerHTML = groupHeader;
      groupDiv.appendChild(gridDiv);
      container.appendChild(groupDiv);
      return;
    }

    dates.forEach(date => {
      const dateCards = productCards.filter(card => card.dataset.date === date);
      if (dateCards.length === 0) return;

      const groupDiv = document.createElement('div');
      groupDiv.className = 'product-group';
      groupDiv.dataset.groupType = 'date';
      groupDiv.dataset.groupValue = date;

      const formattedDate = formatDate(date);
      const groupHeader = `
        <div class="group-header">
          <h3>📅 ${formattedDate}</h3>
          <span class="group-count">${dateCards.length}</span>
        </div>
      `;

      const gridDiv = document.createElement('div');
      gridDiv.className = 'product-grid';
      dateCards.forEach(card => gridDiv.appendChild(card));

      groupDiv.innerHTML = groupHeader;
      groupDiv.appendChild(gridDiv);
      container.appendChild(groupDiv);
    });
  }

  /**
   * Update group visibility based on filters
   */
  function updateGroupVisibility() {
    const groups = document.querySelectorAll('.product-group');

    groups.forEach(group => {
      const visibleCards = Array.from(group.querySelectorAll('.product-card'))
        .filter(card => card.style.display !== 'none');

      if (visibleCards.length === 0) {
        group.style.display = 'none';
      } else {
        group.style.display = '';
        // Update count
        const countSpan = group.querySelector('.group-count');
        if (countSpan) {
          countSpan.textContent = visibleCards.length;
        }
      }
    });
  }

  /**
   * Find nearby venues within specified radius (km)
   */
  function findNearbyVenues(venues, currentVenue, radiusKm) {
    return venues
      .filter(v => v.handle !== currentVenue.handle)
      .map(v => ({
        venue: v,
        distance: calculateDistance(
          currentVenue.coords[0], currentVenue.coords[1],
          v.coords[0], v.coords[1]
        )
      }))
      .filter(v => v.distance <= radiusKm)
      .sort((a, b) => a.distance - b.distance);
  }

  /**
   * Calculate distance between two coordinates using Haversine formula
   */
  function calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 6371; // Earth's radius in km
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  function toRad(degrees) {
    return degrees * (Math.PI / 180);
  }

  /**
   * Get Metro line color
   */
  function getMetroLineColor(line) {
    const colors = {
      'B Line (Red)': '#e30613',
      'D Line (Purple)': '#a05da5',
      'E Line (Expo)': '#00a5e3',
      'A Line (Blue)': '#0070ba',
      'L Line (Gold)': '#f0b62a',
      'C Line (Green)': '#00a84f',
      'K Line (Crenshaw)': '#e96bb3',
      'G Line (Orange)': '#f68b1f'
    };

    for (const [key, color] of Object.entries(colors)) {
      if (line.includes(key.split(' ')[0])) {
        return color;
      }
    }

    return '#94a3b8'; // Default gray
  }

  /**
   * Format date for display
   */
  function formatDate(dateStr) {
    if (!dateStr) return 'Date TBD';

    try {
      const date = new Date(dateStr);
      const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
      return date.toLocaleDateString('en-US', options);
    } catch (e) {
      return dateStr;
    }
  }

  /**
   * Convert string to handle format
   */
  function handleize(str) {
    return str
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  }

  /**
   * Capitalize first letter
   */
  function capitalizeFirst(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  /**
   * Adjust color brightness
   */
  function adjustColorBrightness(color, amount) {
    // Simple hex color adjustment
    let usePound = false;

    if (color[0] === "#") {
      color = color.slice(1);
      usePound = true;
    }

    const num = parseInt(color, 16);
    let r = (num >> 16) + amount;
    let g = ((num >> 8) & 0x00FF) + amount;
    let b = (num & 0x0000FF) + amount;

    r = Math.max(0, Math.min(255, r));
    g = Math.max(0, Math.min(255, g));
    b = Math.max(0, Math.min(255, b));

    return (usePound ? "#" : "") +
      (r << 16 | g << 8 | b).toString(16).padStart(6, '0');
  }

  /**
   * Get asset URL (handles Shopify asset URL format)
   */
  function getAssetUrl(filename) {
    // Try to get from Shopify if available
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root + 'cdn/shop/files/' + filename;
    }

    // Fallback to relative path
    return '/assets/' + filename;
  }

  /**
   * Smooth scroll to element
   */
  function smoothScrollTo(element) {
    if (!element) return;

    const offsetTop = element.offsetTop - 100;
    window.scrollTo({
      top: offsetTop,
      behavior: 'smooth'
    });
  }

  // Export functions for external use
  window.LA2028Venue = {
    filterProducts: handleFilterClick,
    scrollToProducts: function() {
      smoothScrollTo(document.getElementById('products-container'));
    }
  };

})();
